import { Form, Row, Space } from 'antd'
import KDestroyButton from 'app/components/Modules/Form/KDestroyButton'
import KSubmit from 'app/components/Modules/Form/KSubmit'
import { _KFormProps, FormRecord } from 'app/components/Modules/Form/typing'
import Button from 'app/components/UI-Elements/General/Button'
import Portal from 'app/components/UI-Elements/General/Portal'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { GenericObject } from 'app/constants/GlobalTypes'
import KFormContextProvider from 'app/lib/contexts/KFormContext'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useMutation } from 'app/lib/hooks/useMutation'
import { useLazyQuery } from 'app/lib/hooks/useQuery'
import { queries } from 'app/queries'
import { ReactNode, useEffect, useMemo, useState } from 'react'

const defaultQueryResults = {
  execute: () => {
    console.error('No get query implemented')
  },
  loading: false,
  data: {} as GenericObject<string>
}

function KForm<T extends FormRecord>(props: _KFormProps<T>) {
  const { smartTranslation } = ToolBox.useString()
  const { formId: generateFormId } = ToolBox.useId()
  const {
    recordType,
    baseRecordType = recordType,
    resetOnSuccess,
    submitButtonProps = {},
    mutationOptions,
    getParams,
    recordId,
    onSuccess,
    onFailure,
    initialValues,
    inlineDestroy,
    cancelable,
    onCancel,
    portalId,
    initialFetch = true,
    loading,
    onValuesChange,
    children,
    action: queryAction = 'update',
    visualAction,
    ...others
  } = props
  const query = queries[recordType]
  const performGet = useMemo(() => recordId && initialFetch, [initialFetch, recordId])

  const {
    execute: executeGet,
    loading: loadingGet,
    data: dataFetched
  } = query.get ? useLazyQuery<T>(query.get, getParams) : defaultQueryResults

  const action = visualAction || queryAction

  const { loading: loadingUpdate, execute } = useMutation(query[queryAction], {
    recordType,
    action,
    ...mutationOptions
  })
  const [form] = Form.useForm()
  const [hasErrors, setHasErrors] = useState<boolean>(false)

  const data = dataFetched?.[recordType]

  useEffect(() => {
    if (performGet) executeGet()
  }, [performGet])

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data)
    }
  }, [data])

  useEffect(() => {
    hasErrors && setHasErrors(false)
  }, [initialValues])

  const onFinish = async (values: FormRecord) => {
    try {
      const updatedMember = (await execute({
        ...values,
        ...{ [`${baseRecordType}Id`]: recordId }
      })) as { [key in string]: T }

      onSuccess?.(updatedMember?.[recordType])
      resetOnSuccess && form.resetFields()
    } catch (e) {
      onFailure?.(e)
    }
  }

  const onFinishFailed = () => setHasErrors(true)

  const handleValuesChange = (changedValues: Partial<T>, values: T) => {
    onValuesChange?.(changedValues, values)
    setHasErrors(false)
  }

  const formId = generateFormId({ recordType, action })

  const formButtons = () => (
    <Space>
      <If condition={cancelable}>
        <Button
          type="secondary"
          onClick={onCancel && onCancel}
          title={smartTranslation({
            recordType: recordType,
            id: `form.${recordType}.${action}.cancel`
          })}
        />
      </If>
      <If condition={inlineDestroy}>
        <KDestroyButton
          onSuccess={() => onSuccess(data)}
          onFailure={onFailure}
          recordId={recordId}
          recordType={recordType}
        />
      </If>
      <KSubmit
        type={'primary'}
        disabled={hasErrors}
        loading={loadingGet || loadingUpdate || props.loading}
        action={action}
        id={`${recordType}-submit`}
        form={formId}
        {...submitButtonProps}
      />
    </Space>
  )

  return (
    <KFormContextProvider
      loading={loadingGet || loadingUpdate || loading}
      form={form}
      hasErrors={hasErrors}
      {...props}>
      <Form
        onFinish={onFinish}
        form={form}
        onValuesChange={handleValuesChange}
        onFinishFailed={onFinishFailed}
        id={formId}
        initialValues={{ ...data, ...initialValues }}
        scrollToFirstError={{ block: 'center' }}
        {...others}>
        <Row gutter={[{ xs: 0, md: 12 }, 24]}>
          <KCol span={24}>{children as ReactNode}</KCol>
          <Choose>
            <When condition={portalId}>
              <Portal id={portalId}>
                <div className="full-width text-right">{formButtons()}</div>
              </Portal>
            </When>
            <Otherwise>
              <KCol span={24} className="text-right">
                {formButtons()}
              </KCol>
            </Otherwise>
          </Choose>
        </Row>
      </Form>
    </KFormContextProvider>
  )
}
export default KForm
